// Schedules (config/schedule.go; DECISIONS 2026-10-09 "scheduled start and
// stop"): windows a destination, or a group's destinations, are on air.
// Stored as UTC instants; shown and typed in the browser's time zone.
export interface Window {
    start: string
    stop: string
    started?: boolean // the node passed its start: its stop stops them
}

const pad = (n: number) => String(n).padStart(2, '0')

// an instant as a datetime-local input's value, in the browser's zone
export function toLocalInput(iso: string): string {
    const d = new Date(iso)
    if (isNaN(d.getTime())) return ''
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

// a datetime-local input's value as an instant ('' when it is not one)
export function fromLocalInput(v: string): string {
    const d = new Date(v) // a date and time without a zone is local
    return v && !isNaN(d.getTime()) ? d.toISOString() : ''
}

// the window "+ window" adds: after the last, the same hours a day later
// (an event weekend); with none, from the next full hour for two hours
export function newWindow(ws: Window[], now: Date): Window {
    const last = [...ws].sort((a, b) => a.start.localeCompare(b.start)).at(-1)
    if (last) {
        const day = 24 * 3600_000
        return {
            start: new Date(new Date(last.start).getTime() + day).toISOString(),
            stop: new Date(new Date(last.stop).getTime() + day).toISOString(),
        }
    }
    const start = new Date(now)
    start.setMinutes(0, 0, 0)
    start.setHours(start.getHours() + 1)
    return { start: start.toISOString(), stop: new Date(start.getTime() + 2 * 3600_000).toISOString() }
}

// a time near now, shortest first: "14:00" today, "Sat 14:00" this week,
// "10 Oct 14:00" later
export function when(iso: string, now: Date): string {
    const d = new Date(iso)
    const hm = `${pad(d.getHours())}:${pad(d.getMinutes())}`
    if (d.toDateString() === now.toDateString()) return hm
    if (Math.abs(d.getTime() - now.getTime()) < 6 * 24 * 3600_000)
        return `${d.toLocaleDateString('en-GB', { weekday: 'short' })} ${hm}`
    return `${d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} ${hm}`
}

// what a schedule says now: the window it is in, else the next one
export function scheduleNote(ws: Window[] | undefined, now: Date): string {
    const t = now.getTime()
    const live = (ws ?? []).filter((w) => new Date(w.stop).getTime() > t)
    const sorted = live.sort((a, b) => a.start.localeCompare(b.start))
    const cur = sorted.find((w) => new Date(w.start).getTime() <= t)
    if (cur) return `on air until ${when(cur.stop, now)}`
    const next = sorted[0]
    return next ? `next ${when(next.start, now)}–${when(next.stop, new Date(next.start))}` : ''
}

// a schedule's own error, as the node would say it ('' when fine)
export function scheduleError(ws: Window[]): string {
    const sorted = [...ws].sort((a, b) => a.start.localeCompare(b.start))
    for (const [i, w] of sorted.entries()) {
        if (!w.start || !w.stop) return 'a window needs a start and a stop'
        if (w.stop <= w.start) return 'a window stops after it starts'
        if (i > 0 && w.start < sorted[i - 1]!.stop) return 'windows overlap'
    }
    return ''
}
